import { Dialog } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/outline'
import { yupResolver } from '@hookform/resolvers/yup'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useStep } from 'usehooks-ts'
import {
    mappingValidationRules,
    // postMapping,
    removePropertyDuplicates,
    shouldShow,
} from '../helpers/mapping'
import { CSV } from '../helpers/csvparse'
import { generateId, log } from '../helpers/utils'
import { ModalStore } from '../state/ModalStore'
import { InputRow } from './Inputs/InputRow'
import { ProgressBar } from './Progressbar'
import { Stepper, Step } from './Stepper'
import WentWrong from './Modals/WentWrong'
import { XIcon } from '@heroicons/react/solid'
import * as Sentry from '@sentry/browser'
import ConfirmCSVRemove from './Modals/ConfirmCSVRemove'
import { skipTrackingOptions } from '../helpers/skip-tracking'

// Fix Form - New Design
//  conditional validation of zip and state and city based on logic explained here
// https://www.notion.so/open-letter-marketing/Single-Product-Page-Notes-e2626364bffe4f17a35a90bb2a3d9e06#f72f989770e446e5b7e597d00a06df46

const RowHeader = () => {
    return (
        <div className="flex px-4 py-2 justify-center items-center text-sm font-bold">
            <div className="w-20">Match</div>
            <div className="w-60">OLM Destination Field</div>
            <div className="w-60">Source Field</div>
        </div>
    )
}

const ProcessCSVButton = ({ onClick }) => {
    return (
        <button
            onClick={onClick}
            type="button"
            className="bg-olm text-white font-semibold relative flex items-center border border-olm rounded-lg p-2 text-center cursor-pointer hover:bg-olh hover:border-olh focus:outline-none focus:ring-1 focus:ring-olh focus:border-olh sm:text-sm"
            aria-haspopup="listbox"
            aria-expanded="true"
            aria-labelledby="listbox-label">
            <span className="mx-2">Process CSV</span>
            <CheckIcon className="h-6 w-6" />
        </button>
    )
}

const HelperRow = ({ children }) => {
    return (
        <div className="mb-4">
            <p className="mb-2 text-sm text-gray-500"> {children} </p>
        </div>
    )
}

export function MappingSkipTracing({ file, onFinish }) {
    let [isOpen, setIsOpen] = useState(true)
    const [csv, setCSV] = useState([])

    const router = useRouter()
    const slug = router.query.slug

    const [mappingFields, setMappingFields] = useState([])
    const [loading] = useState(false)
    const [loadingPercent] = useState(0)

    const hasBeenForceClosed = useRef(false)

    const totalSteps = 1
    const [currentStep, helpers] = useStep(totalSteps)
    const { canGoToPrevStep, canGoToNextStep, goToNextStep, goToPrevStep } =
        helpers

    const skimModal = ModalStore((state) => state.skim)
    const addModal = ModalStore((state) => state.add)

    const {
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: async function () {
            const values = arguments[0]
            return await yupResolver(
                mappingValidationRules({
                    options: skipTrackingOptions,
                    values,
                    currentStep,
                    slug,
                }),
            )(...arguments)
        },
    })

    const handleCSV = (file) => {
        const complete = (data) => {
            setMappingFields(data.meta.fields)
            data.data = data.data.map((item) => {
                item._id = generateId()
                return item
            })
            setCSV(data)
        }
        CSV.parse({ file, complete })
    }

    function prepareRowWithHeadersData(item, mappedFields) {
        const row = item
        let rowObject = {}

        for (const header of Object.keys(mappedFields)) {
            rowObject[header] = row[mappedFields[header]]
        }

        // rowObject = { ...rowObject } // add smarty street data

        return rowObject
    }

    const generateMergedCSVFileJSON = (data, mappedFields) => {
        let valids = []

        // seperate
        data.forEach((item) => {
            const itemWithHeaders = prepareRowWithHeadersData(
                item,
                mappedFields,
            )
            valids.push(itemWithHeaders)
        })

        const originalTotalRows = valids.length

        // valids = valids.map((item, idx) => postMapping(item, idx))

        // We need to remove duplicates by property address, which is madatory
        valids = removePropertyDuplicates(valids)

        // log('valids', valids)

        // // send back data
        onFinish({
            originalTotalRows,
            file,
            data: {
                valids,
                invalids: [],
            },
        })
    }

    const handleFormSubmission = (mappedFields) => {
        try {
            generateMergedCSVFileJSON(csv.data, mappedFields)
            skimModal()
        } catch (e) {
            log(e)
            handleError(e)
        }
    }

    const handleError = (e) => {
        log(e)
        Sentry.captureException(e)
        terminateProcess(e)
        // log(e)
        // if (e.name === 'TypeError') {
        //     terminateProcess()
        // }
    }

    const terminateProcess = (e) => {
        skimModal()
        const msg = e?.response?.data || { message: 'Something went wrong.' }
        log(e?.response?.data)
        if (e.name === 'TypeError') {
            addModal(
                <WentWrong title={'Something went wrong'} msg={msg.message} />,
            )
        }
    }

    const nextStep = () => {
        handleSubmit((data) => {
            canGoToNextStep ? goToNextStep() : handleFormSubmission(data)
        })()
    }

    const handleDelete = () => {
        hasBeenForceClosed.current = true
        skimModal() // Remove this modal
    }

    const deleteFile = () => {
        addModal(<ConfirmCSVRemove onConfirm={handleDelete} />)
    }

    /**
     * @NOTWORKING
     */
    const handlEscKey = (e) => {
        if (e.keyCode == 27) {
            e.stopImmediatePropagation()
        }
    }

    useEffect(() => {
        document.addEventListener('keydown', handlEscKey)
        return document.removeEventListener('keydown', handlEscKey)
    }, [])

    useEffect(() => {
        handleCSV(file)
    }, [file])

    useEffect(() => {
        log('errors', errors)
    }, [errors])

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={() => setIsOpen(false)}
                className="fixed z-10 inset-0 overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen">
                    <Dialog.Overlay className="fixed inset-0 bg-black opacity-30 pointer-events-none" />

                    <div className="relative bg-white rounded mx-auto min-h-screen w-full max-w-3xl">
                        <Dialog.Title className="p-2 flex justify-center items-center relative">
                            <div className="z-20 absolute top-2 left-2 right-14 bg-olm-green text-white font-semibold flex items-center border border-olm-green rounded-lg p-2 text-center text-sm">
                                <span className="mx-2">{file.path}</span>
                            </div>
                            <button
                                onClick={deleteFile}
                                type="button"
                                className="z-20 absolute top-2 right-2 bg-gray-200 text-gray-500 font-semibold flex items-center border border-gray-200 rounded-lg p-2 text-center cursor-pointer hover:bg-olh hover:border-olh hover:text-white focus:outline-none focus:ring-1 focus:ring-olh focus:border-olh sm:text-sm">
                                <XIcon className="h-5" />
                            </button>
                        </Dialog.Title>

                        <div className="overflow-x-auto w-full m-auto p-2">
                            <div className="m-auto w-[600px] space-y-10">
                                <div className="flex justify-center mt-5"></div>
                                {loading && (
                                    <div className="space-y-3">
                                        <p>
                                            The system is validating addresses,
                                            and if selected, removing duplicate
                                            property and mailing addresses, and
                                            removing undeliverables.
                                        </p>
                                        <ProgressBar
                                            progressPercentage={loadingPercent}
                                        />
                                    </div>
                                )}
                                <div className={`${loading && 'hidden'}`}>
                                    <Stepper
                                        config={{
                                            errors,
                                            currentStep,
                                            totalSteps,
                                            goToPrevStep,
                                            nextStep,
                                            canGoToPrevStep,
                                            canGoToNextStep,
                                            stickyFooter: true,
                                            finalButton: (
                                                <ProcessCSVButton
                                                    onClick={nextStep}
                                                    className="my-2"
                                                />
                                            ),
                                        }}>
                                        <Step index={1} title="Field Mapping">
                                            <HelperRow>
                                                We will match the fields below
                                                based on your column titles.
                                                Please make sure that all fields
                                                are accurate and edit as
                                                necessary.
                                            </HelperRow>

                                            <RowHeader />

                                            {skipTrackingOptions &&
                                                skipTrackingOptions.map(
                                                    (item, idx) => {
                                                        return (
                                                            shouldShow(
                                                                item,
                                                                slug,
                                                            ) && (
                                                                <InputRow
                                                                    errors={
                                                                        errors
                                                                    }
                                                                    setValue={
                                                                        setValue
                                                                    }
                                                                    mappingFields={
                                                                        mappingFields
                                                                    }
                                                                    key={idx}
                                                                    item={item}
                                                                />
                                                            )
                                                        )
                                                    },
                                                )}

                                            <div className="pt-4 border-t-2 text-sm border-olm">
                                                <b>
                                                    IMPORTANT! PLEASE READ
                                                    FULLY! – NO REFUNDS CAN BE
                                                    GIVEN FOR INCOMPLETE OR
                                                    INACCURATE DATA FIELD
                                                    MAPPING!
                                                </b>
                                                <br />
                                                <br />
                                                By clicking <b>Process CSV</b>,
                                                you are agreeing that the
                                                columns in your uploaded lead
                                                list correctly match the data
                                                points required for a skip
                                                trace. Your records should only
                                                contain individual people and
                                                exclude any companies or trusts.
                                                The sample record below
                                                references the necessary data
                                                points needed in order to
                                                provide accurate results. For
                                                added ease, a skip trace
                                                template is available for
                                                download on this page.
                                            </div>
                                        </Step>
                                    </Stepper>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    )
}
